import { DocumentProperty } from 'types/dynamic-forms/DocumentProperty';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';
import { iterateErrors, validateDocument } from './validation';
import { resolveBoolRule } from './boolRule';
import { DocumentDataSourceOption } from 'types/dynamic-forms/DocumentDataSourceOption';
import { Patch, produceWithPatches } from 'immer';
import { getDocumentDataSource, putDigitalVersion } from 'services/api/dynamicFormsApi';
import { QUERY_KEYS } from 'services/api/constants';
import queryClient from 'util/queryClient';
import { documentDataSourcesQueryKey } from '../documentQueries';
import createPatchRequestsBatcher from './createPatchRequestsBatcher';
import createPromiseQueue from './createPromiseQueue';
import { DocumentModelState } from 'types/dynamic-forms/DocumentModelState';
import { DocumentPropertyType } from 'types/dynamic-forms/DocumentPropertyType';
import { DocumentPropertyFieldsAreaId } from 'types/dynamic-forms/DocumentPropertyFieldsAreaId';
import { ResolveValueContext } from './ResolveValueContext';
import { DocumentModelMetadata } from 'types/dynamic-forms/DocumentModelMetadata';
import createEventEmitter from 'util/createEventEmitter';
import { DocumentDependenciesTracker } from './DocumentDependenciesTracker';

// The PCR and c-PCR of the document. They belong to its digital version, not to the document data, so they are
// read from the metadata and changed only through the digital version switch.
export type PcrSelection = { pcrId: string | null; cpcrId: string | null };

// Key of the "PCR is required" error in the errors tree; the backend reports it under the same name.
export const PCR_ERROR_KEY = 'pcrId';

const PCRS_DATA_SOURCE_ID = 'pcrs';
const CPCRS_DATA_SOURCE_ID = 'cpcrs';
const PRODUCT_CATEGORY_PROPERTY_ID = 'productCategoryId';

type StoreData = {
  docId: string;
  initialData: any;
  initialSchemaId: string;
  initialDataSources: Record<string, DocumentDataSourceOption[]>;
  state: DocumentModelState;
  metadata: DocumentModelMetadata;
  properties: DocumentProperty[];
};

export default function createDocumentStore({
  docId,
  initialData,
  initialSchemaId,
  initialDataSources,
  state,
  metadata,
  properties,
}: StoreData) {
  const synchronize = createPromiseQueue();
  const patchBatch = createPatchRequestsBatcher(docId, {
    throttleMs: 1000,
    retryDelaysMs: [500, 1000, 2000, 5000],
    synchronize,
    onPatchesFlushed: (patches) => {
      if (patches.some((patch) => patch.path === '/verificationType')) {
        reloadData();
      }
    },
    // Refetch the DOC query so the recreated store carries the server's values and state flags.
    onWriteRefused: reloadData,
  });
  const depsTracker = new DocumentDependenciesTracker(properties);
  let data = initialData;
  const changeEmitter = createEventEmitter<void>();
  let dataSources = initialDataSources;
  let dataSourcesOptionsCacheByParents = new Map<string, Map<string, DocumentDataSourceOption[]>>();
  let schemaId = initialSchemaId;
  let pcrSelection: PcrSelection = { pcrId: metadata.pcrId ?? null, cpcrId: metadata.cpcrId ?? null };
  let errorsTree = getValidationResult();

  function getValue<T = any>(path: JsonPointerPath): T {
    let nextNode = data;
    for (const segment of path) {
      nextNode = nextNode[segment];
      if (nextNode === undefined) {
        const prop = getPropertyOrThrow(path);
        return getPropertyDefaultValue(prop) as T;
      }
    }
    return nextNode;
  }

  /**
   * Like `getValue`, but for a property the current schema may not declare (for example `cpcrId`,
   * which only some schemas have): returns `undefined` instead of throwing when the schema has no
   * property at `path`. A value left in the data for an undeclared property is ignored.
   */
  function tryGetValue<T = any>(path: JsonPointerPath): T | undefined {
    if (!findProperty(path)) {
      return undefined;
    }
    return getValue<T>(path);
  }

  function getPropertyDefaultValue(prop: DocumentProperty) {
    if (prop.defaultValue !== undefined) {
      return prop.defaultValue;
    }
    switch (prop.type) {
      case DocumentPropertyType.TEXT:
        return '';
      case DocumentPropertyType.NUMBER:
        return null;
      case DocumentPropertyType.ENUM:
        return null;
      case DocumentPropertyType.DATE:
        return null;
      case DocumentPropertyType.ARRAY:
        return [];
      case DocumentPropertyType.OBJECT:
        return {};
      default:
        throw new Error(`Default value for "${prop.type}" is not defined.`);
    }
  }

  function getValidationResult() {
    const result = validateDocument(getResolveValueContext());
    // No schema rule can require the PCR, because it is not a document property. Like a schema rule, it only
    // counts while the Main area (the PCR selector) is editable.
    if (!pcrSelection.pcrId && !getIsPcrDisabled()) {
      result.set(PCR_ERROR_KEY, { ownError: 'Required' });
    }
    return result;
  }

  function getIsPcrDisabled(): boolean {
    return !state.fields[DocumentPropertyFieldsAreaId.Main];
  }

  function getPropertyOrThrow(path: JsonPointerPath): DocumentProperty {
    let currentPropertiesArray = properties;
    for (let i = 0; i < path.length - 1; i++) {
      let propertyId = path[i];
      if (typeof propertyId === 'number') {
        continue;
      }

      const property = currentPropertiesArray.find((p) => p.propertyId === propertyId);
      if (!property) {
        throw new Error(`Property ${propertyId} doesn't exist in parent at path ${JSON.stringify(path)}.`);
      }
      if (!property.children) {
        throw new Error(`Property ${propertyId} doesn't have children at path ${JSON.stringify(path)}.`);
      }

      currentPropertiesArray = property.children;
    }

    const prop = currentPropertiesArray.find((p) => p.propertyId === path[path.length - 1]);
    if (!prop) {
      throw new Error(`Property ${JSON.stringify(path)} doesn't exist.`);
    }

    return prop;
  }

  // Non-throwing counterpart of getPropertyOrThrow: undefined when the schema has no property at `path`.
  function findProperty(path: JsonPointerPath): DocumentProperty | undefined {
    let currentPropertiesArray: DocumentProperty[] | undefined = properties;
    for (let i = 0; i < path.length - 1; i++) {
      const propertyId = path[i];
      if (typeof propertyId === 'number') {
        continue;
      }
      currentPropertiesArray = currentPropertiesArray.find((p) => p.propertyId === propertyId)?.children;
      if (!currentPropertiesArray) {
        return undefined;
      }
    }
    return currentPropertiesArray.find((p) => p.propertyId === path[path.length - 1]);
  }

  function getIsVisible(path: JsonPointerPath): boolean {
    const prop = getPropertyOrThrow(path);
    if (!prop.visibilityRule) {
      return true;
    }
    return resolveBoolRule(prop.visibilityRule, prop, path, getResolveValueContext());
  }

  function getIsRemovalForced(path: JsonPointerPath): boolean {
    const prop = getPropertyOrThrow(path);
    if (!prop.forceRemovalRule) {
      return false;
    }
    return resolveBoolRule(prop.forceRemovalRule, prop, path, getResolveValueContext());
  }

  function getIsAreaDisabled(path: JsonPointerPath): boolean {
    const area = getFieldsAreaId(path);
    return !state.fields[area];
  }

  function getIsDisabled(path: JsonPointerPath): boolean {
    if (getIsAreaDisabled(path)) {
      return true;
    }
    // A `disabledRule` makes the input read-only without skipping validation (see shouldIgnoreValidation).
    const prop = getPropertyOrThrow(path);
    if (prop.disabledRule) {
      return resolveBoolRule(prop.disabledRule, prop, path, getResolveValueContext());
    }
    return false;
  }

  /**
   * A property is required when the schema declares a validation rule for it
   * and that validation is not currently skipped (hidden, disabled area or
   * `validationRuleDisabled`). Keeping this in the store guarantees the UI marker
   * and the actual validation always agree.
   */
  function getIsRequired(path: JsonPointerPath): boolean {
    const prop = getPropertyOrThrow(path);
    if (!prop.validationRule) {
      return false;
    }
    return !shouldIgnoreValidation(path);
  }

  function getFieldsAreaId(path: JsonPointerPath): DocumentPropertyFieldsAreaId {
    const prop = getPropertyOrThrow([path[0]]);
    return prop.fieldsAreaId || DocumentPropertyFieldsAreaId.Main;
  }

  function shouldIgnoreValidation(path: JsonPointerPath): boolean {
    const prop = getPropertyOrThrow(path);
    const isVisible = getIsVisible(path);
    if (!isVisible) {
      return true;
    }
    // A `disabledRule` makes the field read-only but keeps its validation active. An area-disable
    // normally skips validation, unless the schema keeps the property's own validation active for
    // step completion (nested properties still use their own setting).
    const isDisabled = getIsAreaDisabled(path);
    const rootProperty = getPropertyOrThrow([path[0]]);
    if (isDisabled && !rootProperty.includeInStepCompletionWhenDisabled) {
      return true;
    }
    if (prop.validationRuleDisabled) {
      if (resolveBoolRule(prop.validationRuleDisabled, prop, path, getResolveValueContext())) {
        return true;
      }
    }
    return false;
  }

  function getRefValue(prop: DocumentProperty, path: JsonPointerPath, refIndex: number): any {
    const ref = prop.refs?.[refIndex];
    if (!ref) {
      throw new Error(`Ref with index "${refIndex}" doesn't exist for property "${prop.propertyId}".`);
    }

    let refPath: JsonPointerPath;
    if (ref.refType === 'rootProp') {
      refPath = [ref.propertyId];
    } else if (ref.refType === 'siblingProp') {
      refPath = [...path];
      refPath[refPath.length - 1] = ref.propertyId;
    } else {
      throw new Error(`Unknown ref: ${JSON.stringify(ref)}.`);
    }

    return getValue(refPath);
  }

  function getOptions(path: JsonPointerPath): DocumentDataSourceOption[] {
    const prop = getPropertyOrThrow(path);
    if (!prop.dataSourceId) {
      throw new Error(`Property at path "${JSON.stringify(path)}" doesn't have a dataSourceId.`);
    }
    const dataSource = dataSources[prop.dataSourceId];
    if (!dataSource) {
      throw new Error(`Data source with id "${prop.dataSourceId}" doesn't exist.`);
    }

    if (prop.parentDataSourceRefId === undefined) {
      return dataSource;
    }

    const parentDataSourceRefValue = getRefValue(prop, path, prop.parentDataSourceRefId);
    return getChildOptions(prop.dataSourceId, dataSource, parentDataSourceRefValue);
  }

  // The options of a data source whose parent option is `parentValue`. Cached, so a caller gets the same array
  // until the data source changes: filtering on each call would be expensive and break useSyncExternalStore.
  function getChildOptions(
    dataSourceId: string,
    dataSource: DocumentDataSourceOption[],
    parentValue: any
  ): DocumentDataSourceOption[] {
    let cacheEntryForDataSource = dataSourcesOptionsCacheByParents.get(dataSourceId);
    if (!cacheEntryForDataSource) {
      cacheEntryForDataSource = new Map<string, DocumentDataSourceOption[]>();
      dataSourcesOptionsCacheByParents.set(dataSourceId, cacheEntryForDataSource);
    }
    let options = cacheEntryForDataSource.get(parentValue);
    if (!options) {
      options = dataSource.filter((o) => o.parentDataSourceValue === parentValue);
      cacheEntryForDataSource.set(parentValue, options);
    }
    return options;
  }

  // The PCRs of the selected product category, as the PCR property offered them when it was document data.
  function getPcrOptions(): DocumentDataSourceOption[] {
    const productCategoryId = tryGetValue<string | null>([PRODUCT_CATEGORY_PROPERTY_ID]) ?? null;
    return getChildOptions(PCRS_DATA_SOURCE_ID, getDataSourceById(PCRS_DATA_SOURCE_ID), productCategoryId);
  }

  // The c-PCRs linked to the given PCR.
  function getCpcrOptions(pcrId: string | null): DocumentDataSourceOption[] {
    return getChildOptions(CPCRS_DATA_SOURCE_ID, getDataSourceById(CPCRS_DATA_SOURCE_ID), pcrId);
  }

  function getDataSourceById(dataSourceId: string): DocumentDataSourceOption[] {
    const dataSource = dataSources[dataSourceId];
    if (!dataSource) {
      return [];
    }
    return dataSource;
  }

  function getResolveValueContext(): ResolveValueContext {
    return {
      getValue,
      getRefValue,
      properties,
      getOptions,
      hasPublishedVersionHistory: metadata.hasPublishedVersionHistory ?? false,
      documentStatus: metadata.documentStatus,
      versionState: metadata.versionState ?? null,
      shouldIgnoreValidation,
    };
  }

  async function invalidateDataSource(path: JsonPointerPath) {
    const prop = getPropertyOrThrow(path);
    if (!prop.dataSourceId) {
      throw new Error(`Property at path "${JSON.stringify(path)}" doesn't have a dataSourceId.`);
    }
    const newOptions = await getDocumentDataSource(schemaId, prop.dataSourceId, docId);
    dataSources = { ...dataSources, [prop.dataSourceId]: newOptions };
    dataSourcesOptionsCacheByParents.clear();
    // Validation reads the options (in-options check, a disabled verifier), so it is stale until rerun.
    errorsTree = getValidationResult();
    changeEmitter.emitEvent();
  }

  function reloadData() {
    // Code smell: reload useQuery, so we can recreate the document store in the calling code.
    queryClient.invalidateQueries({ queryKey: ['DOC', docId] });
    // Data sources depend on the stored document as well: companyVerifiers offers a discontinued verifier
    // only while the current version stores it, so options cached for the previous data would lack it.
    // The schemaId may just have changed, so the document prefix is matched, covering every schema and
    // version, the current-version query included. Other documents' data sources stay cached.
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DYNAMIC_FORMS_DOCUMENT_DATA_SOURCES(docId) });
  }

  function setValue(path: JsonPointerPath, value: any): void {
    const [newData, patches] = setPropertyWithPatches(path, value);
    data = newData;

    // When previously filled in properties become invisible, we want to remove them from the document data. This fixes transitive "refs" dependencies.
    // Additionally, enums with dynamic options might need to be cleaned up if their current value is no longer in the datasource. This automatically fixes misaligned enum values which fail validation.
    // A dependent property whose fields area is locked is never removed: the backend would reject the whole patch
    // batch for it, so its value stays in the data while hidden. Server-side cleanup is the backend's job.
    const prop = getPropertyOrThrow(path);
    const dependentPaths = depsTracker.collectDependentJsonPointerPaths(prop, path, newData);
    const pathsToDelete = dependentPaths.filter((p) => {
      if (getIsAreaDisabled(p)) {
        return false;
      }
      const dependentProperty = getPropertyOrThrow(p);
      const becameHidden = !getIsVisible(p);
      return (becameHidden && !dependentProperty.preserveWhenHidden) || isDependentEnumWhoseValueIsNoLongerInOptions(p);
    });
    const [newDataAfterCleanup, patchesWithDeletes] = deletePropertiesWithPatches(pathsToDelete);
    data = newDataAfterCleanup;
    patches.push(...patchesWithDeletes);

    // No-op update (same value and no cleanup deletions): skip sync/status updates.
    if (patches.length === 0) {
      return;
    }

    // - Optimization opportunity 1: avoid full document revalidation.
    //   Instead revalidate only edited and dependent properties via "DocumentProperty.refs".
    // - Optimization opportunity 2: cache and incrementally revalidate bool rules (visibilty and disabled validation).
    errorsTree = getValidationResult();

    changeEmitter.emitEvent();

    const rfc6902Patches = patches.map((p) => ({ ...p, path: '/' + p.path.join('/') }));

    patchBatch.enqueuePatches(rfc6902Patches);
  }

  function setPropertyWithPatches(path: JsonPointerPath, value: any) {
    return produceWithPatches(data, (data: any) => {
      let currentData = data;
      for (let i = 0; i < path.length - 1; i++) {
        const segment = path[i];
        let nextData = currentData[segment];
        if (nextData === undefined) {
          nextData = typeof path[i + 1] === 'number' ? [] : {};
          currentData[segment] = nextData;
        }
        currentData = nextData;
      }
      currentData[path[path.length - 1]] = value;
    }) as unknown as [any, Patch[], Patch[]];
  }

  function deletePropertiesWithPatches(paths: JsonPointerPath[]) {
    if (paths.length === 0) {
      return [data, []];
    }

    return produceWithPatches(data, (draft: any) => {
      for (const path of paths) {
        let current = draft;
        for (let i = 0; i < path.length - 1; i++) {
          current = current[path[i]];
          if (current === undefined) {
            break;
          }
        }
        if (current !== undefined) {
          delete current[path[path.length - 1]];
        }
      }
    }) as unknown as [any, Patch[], Patch[]];
  }

  function isDependentEnumWhoseValueIsNoLongerInOptions(path: JsonPointerPath): boolean {
    const prop = getPropertyOrThrow(path);
    if (typeof prop.parentDataSourceRefId !== 'number') {
      return false;
    }

    const value = getValue<string | null>(path);
    if (value === null) {
      return false;
    }

    const options = getOptions(path);
    return !options.find((o) => o.value === value);
  }

  return {
    docId,
    state,
    metadata,
    setValue,
    getValue,
    tryGetValue,
    getIsVisible,
    getIsRemovalForced,
    getIsDisabled,
    getIsRequired,
    getFieldsAreaId,
    getErrorMessage(path: JsonPointerPath) {
      if (path.length === 0) {
        throw new Error("Empty path was provided: document root doesn't have any validation rules. Probably, a mistake.");
      }
      if (typeof path[0] !== 'string') {
        throw new Error(`Path must start with a propertyId ${JSON.stringify(path)}.`);
      }

      let currentNode = errorsTree.get(path[0]);
      for (let i = 1; i < path.length && currentNode; i++) {
        const segment = path[i];
        currentNode = currentNode.childrenErrors?.get(segment);
      }
      return currentNode?.ownError ?? null;
    },
    getErrorMessagesIncludingNested(path: JsonPointerPath): Iterable<string> {
      if (path.length === 0) {
        return iterateErrors({ ownError: null, childrenErrors: errorsTree });
      }
      if (typeof path[0] !== 'string') {
        throw new Error(`Path must start with a propertyId ${JSON.stringify(path)}.`);
      }

      let currentNode = errorsTree.get(path[0]);
      for (let i = 1; i < path.length && currentNode; i++) {
        const segment = path[i];
        currentNode = currentNode.childrenErrors?.get(segment);
      }
      if (!currentNode) {
        return [];
      }

      return iterateErrors(currentNode);
    },
    // True when any property in one of the given fields-areas has a (own or nested) validation error.
    // Used to gate an action on a subset of the form — e.g. Start Verification depends on steps 1-4
    // and validates the configured document-input areas, mirroring the backend.
    hasErrorsInAreas(areas: DocumentPropertyFieldsAreaId[]): boolean {
      if (areas.includes(DocumentPropertyFieldsAreaId.Main) && errorsTree.get(PCR_ERROR_KEY)?.ownError) {
        return true;
      }
      for (const prop of properties) {
        const area = prop.fieldsAreaId || DocumentPropertyFieldsAreaId.Main;
        if (!areas.includes(area)) {
          continue;
        }
        const node = errorsTree.get(prop.propertyId);
        if (!node) {
          continue;
        }
        for (const _ of iterateErrors(node)) {
          return true;
        }
      }
      return false;
    },
    getPcrSelection(): PcrSelection {
      return pcrSelection;
    },
    getPcrOptions,
    getCpcrOptions,
    getIsPcrDisabled,
    // Moves the document to the digital version of the selection. Queued after the pending patches, so the backend
    // sees the saved data. When the schema changes the form is reloaded, because the new schema has other fields.
    async setDigitalVersion(selection: PcrSelection, reloadAfterSave: boolean): Promise<void> {
      pcrSelection = selection;
      errorsTree = getValidationResult();
      changeEmitter.emitEvent();

      await synchronize(() => putDigitalVersion(docId, selection));
      if (reloadAfterSave) {
        // The data sources are keyed by the document, not the schema, so the ones cached belong to the old
        // schema. Resetting drops them, and the page waits for the new ones instead of rendering with the old.
        void queryClient.resetQueries({ queryKey: documentDataSourcesQueryKey(docId) });
        reloadData();
      }
    },
    getSchemaId(): string {
      return schemaId;
    },
    addDataChangedListener: changeEmitter.addListener,
    addDataSyncStatusListener: patchBatch.statusEmitter.addListener,
    getOptions,
    getDataSourceById,
    invalidateDataSource,
    synchronize,
    reloadData,
  };
}
